// src/private/ZoneLayer.js
import React, { useState, useCallback } from "react";
import { useViewport } from "react-flow-renderer";
import { DT_ZONES, ZONE_BASE_POSITIONS, AREA_BOUNDS, KANBAN_TOP } from "./dtZones";
import ScenarioTimeline, { TIMELINE_ORIGIN_OFFSET } from "./ScenarioTimeline";
import ScenarioMatrix from "./ScenarioMatrix";
// useT from langContext (NOT LanguageContext.jsx) — see ScenarioTimeline.jsx's
// comment: this module is imported by scenarioCardGrid.js (for
// SCENARIO_MIN_W), and scenarioCardGrid.test.js imports it directly under
// jsdom; LanguageContext.jsx transitively pulls in firebase-config.jsx,
// which breaks there. langContext.js has no firebase import.
import { useT } from "../i18n/langContext";
import "./ZoneLayer.css";

const COL_W = 400; // matches the snap hit-zone (base.x-40 .. base.x+COLUMN_W) so the
                   // drawn column box lines up with where a drop snaps
const COL_TOP_PAD = 146; // header chip + evidence band space above the first card
                         // (bumped +50 from 96, live-verified with Playwright)
const COL_BOTTOM_MARGIN = 40; // gutter between the last card row and area 03's own bottom edge
const AREA_GAP = 140; // horizontal clearance between one area's right edge and the next's left edge
// scenari_futuri's own wider floor for area 02, independent of tree size.
// Exported so InfiniteCanvas.js can size the card-placement grid against
// the same floor without needing this component's own scenarioSize state.
export const SCENARIO_MIN_W = 3200;
const MATRIX_AREA_W = 1100; // matrix area's own width — square-ish uncertainty grid,
                             // not the full brainstorm width (it sits beside it now,
                             // not stacked below it)

export default function ZoneLayer({
  evidenceChips = {},
  sessionMode,
  scenarioSplits = [],
  scenarioBranches = [],
  onAddScenarioSplit,
  onAcceptScenarioSplit,
  onDismissScenarioSplit,
  onRenameScenarioBranch,
  onDeleteScenarioBranch,
  matrixAxisLabels,
  onAxisLabelChange,
  onUpdateBranchMatrixPos,
  onUpdateBranchStyle,
  onExportArea,
  cardsGridHeight = 0,
  notesGridHeight = 0,
  onScenarioTreeSizeChange,
}) {
  const { x, y, zoom } = useViewport();
  const t = useT();
  const B = AREA_BOUNDS;

  // Real rendered size of the scenario tree, reported by ScenarioTimeline
  // itself (see its onSizeChange) using its ACTUAL measured pill widths —
  // not a guess made from outside the component. Starts at the nominal
  // brainstorm size (0-content default) and grows via the callback as soon
  // as the tree first renders, so "03 Brainstorming"'s background always
  // covers exactly what the tree needs, in both width and height.
  const [scenarioSize, setScenarioSize] = useState({ width: 0, height: 0 });
  const handleScenarioSizeChange = useCallback((size) => {
    setScenarioSize((prev) => (prev.width === size.width && prev.height === size.height ? prev : size));
    // Mirrored up to InfiniteCanvas so the mentor's card-placement grid can
    // start below the tree's actual current height instead of a fixed
    // offset that only matched the tree at 0 content — see
    // scenarioCardGrid.js's onScenarioTreeSizeChange usage.
    if (onScenarioTreeSizeChange) onScenarioTreeSizeChange(size);
  }, [onScenarioTreeSizeChange]);

  const isScenarioMode = sessionMode === 'scenari_futuri';
  // 60 mirrors ScenarioTimeline.jsx's own originX offset (bounds.x + 60).
  // Scenario trees read cramped at the tradizionale floor even before any
  // forks exist (a branching layout wants room to breathe more than a
  // kanban board does) — SCENARIO_MIN_W gives it a wider starting floor;
  // it still grows further from there as the tree itself grows.
  const brainstormWidth = isScenarioMode
    ? Math.max(B.brainstorm.w, SCENARIO_MIN_W, 60 + scenarioSize.width + AREA_GAP)
    : B.brainstorm.w;
  // cardsGridHeight/notesGridHeight (area-local, from InfiniteCanvas's own
  // live measurement of where mentor-placed cards/notes currently sit —
  // its two bottom-row columns of the T layout) fold into the same max()
  // as the tree: whichever of "the tree", "the cards column", or "the
  // notes column" currently needs more vertical room wins — the two
  // columns sit side by side, so they never need to ADD, only the taller
  // of the two matters. Because this recomputes from live node positions
  // every render, not a ratchet/counter, the area shrinks back down on its
  // own the moment content is removed and nothing else needs that space,
  // with the same floor guaranteed by B.brainstorm.h either way.
  const brainstormHeight = isScenarioMode
    ? Math.max(
        B.brainstorm.h,
        TIMELINE_ORIGIN_OFFSET + scenarioSize.height + AREA_GAP,
        cardsGridHeight + AREA_GAP,
        notesGridHeight + AREA_GAP
      )
    : B.brainstorm.h;

  // Layout order is left-to-right: 01 Mazzo, 02 Brainstorming, then either
  // 03 Lavagna (nominal) or — in scenari_futuri, once a matrix exists —
  // 03 Matrice / 04 Lavagna. Lavagna always trails whatever precedes it, so
  // its x is computed here rather than read as a fixed AREA_BOUNDS value: a
  // wide scenario tree growing brainstormWidth pushes both the matrix and
  // lavagna right with it, live, every render.
  const matrixArea = isScenarioMode
    ? {
        x: B.brainstorm.x + brainstormWidth + AREA_GAP,
        y: B.brainstorm.y,
        w: MATRIX_AREA_W,
        h: brainstormHeight,
      }
    : null;
  const lavagnaX = isScenarioMode
    ? matrixArea.x + matrixArea.w + AREA_GAP
    : B.brainstorm.x + brainstormWidth + AREA_GAP;
  // Per-area export buttons (every mode) and, in scenari_futuri, the
  // scenario timeline/matrix are the interactive content this layer holds
  // (dt-zone/evidence chips stay decorative, pointer-events: none
  // throughout). This layer's base CSS z-index is always `auto` (ZoneLayer.css)
  // so it never establishes its own stacking context — a non-auto z-index
  // here would trap every descendant below react-flow's own internal
  // renderer (z-index 4, see react-flow-renderer/dist/style.css) no matter
  // what z-index a descendant sets, since a child's z-index only matters for
  // comparisons *inside* the parent's context, not against contexts outside
  // it. Bumping the whole layer's z-index (an earlier version of this fix)
  // escaped the trap but dragged every other child along with it —
  // including the opaque, unconditionally-rendered deck panel — hiding the
  // card deck behind it. Instead, only `.zone-layer__interactive-layer`
  // (below) carries an explicit z-index, so only its own children escape to
  // compete with react-flow's renderer — the deck/lavagna panels have no
  // z-index of their own and stay exactly where they were. Verified via
  // Playwright: elementFromPoint resolved to .react-flow__pane until this
  // was fixed.

  return (
    <div className="zone-layer">
      <div
        className="zone-layer__inner"
        aria-hidden="true"
        style={{ transform: `translate(${x}px, ${y}px) scale(${zoom})` }}
      >
        {/* ---- region panels ---- */}
        <div
          className="area-region area-region--deck"
          style={{ left: B.deck.x, top: B.deck.y, width: B.deck.w, height: B.deck.h }}
        />
        <div
          className="area-region area-region--brainstorm"
          style={{ left: B.brainstorm.x, top: B.brainstorm.y, width: brainstormWidth, height: brainstormHeight }}
        />
        {isScenarioMode && (
          <div
            className="area-region area-region--matrix"
            style={{ left: matrixArea.x, top: matrixArea.y, width: matrixArea.w, height: matrixArea.h }}
          />
        )}
        <div
          className="area-region area-region--lavagna"
          style={{ left: lavagnaX, top: B.lavagna.y, width: B.lavagna.w, height: B.lavagna.h }}
        />

        {/* ---- region labels ---- */}
        <div className="area-band" style={{ left: B.deck.x + 24, top: B.deck.y - 70 }}>
          <span className="area-band__kicker">01</span>
          <span className="area-band__title">{t('canvas.areas.deck')}</span>
        </div>
        <div className="area-band" style={{ left: B.brainstorm.x + 24, top: B.brainstorm.y - 70 }}>
          <span className="area-band__kicker">02</span>
          <span className="area-band__title">{isScenarioMode ? t('canvas.areaJump.scenariFuturi') : t('canvas.areas.brainstorm')}</span>
        </div>
        {isScenarioMode && (
          <div className="area-band" style={{ left: matrixArea.x + 24, top: matrixArea.y - 70 }}>
            <span className="area-band__kicker">03</span>
            <span className="area-band__title">{t('canvas.areas.matrix')}</span>
          </div>
        )}
        <div className="area-band" style={{ left: lavagnaX + 24, top: B.lavagna.y - 70 }}>
          <span className="area-band__kicker">{isScenarioMode ? "04" : "03"}</span>
          <span className="area-band__title">{t('canvas.areas.lavagna')}</span>
        </div>

        {/* ---- phase columns (kanban) — tradizionale mode only ---- */}
        {sessionMode !== 'mappatura' && sessionMode !== 'scenari_futuri' && DT_ZONES.map((z) => {
          const base = ZONE_BASE_POSITIONS[z.key];
          const chips = evidenceChips[z.key] || [];
          return (
            <div
              key={z.key}
              className="dt-zone"
              style={{
                left: base.x - 40,
                top: KANBAN_TOP - COL_TOP_PAD,
                width: COL_W,
                // Was a flat `B.brainstorm.h - 140` — that 140 assumed the
                // column's top offset from the area's own top edge was 140px,
                // but it's actually (KANBAN_TOP - COL_TOP_PAD) - B.brainstorm.y
                // = 164px, so columns ran 24px past area 03's bottom edge.
                // Compute the real available space instead of a stale guess.
                height: (B.brainstorm.y + B.brainstorm.h) - (KANBAN_TOP - COL_TOP_PAD) - COL_BOTTOM_MARGIN,
                "--zone-color": z.color,
              }}
            >
              <span className="dt-zone__chip">
                <span className="dt-zone__icon">{z.icon}</span>
                {t(`canvas.zones.${z.key}`)}
              </span>
              {chips.length > 0 && (
                <div className="dt-zone__evidence-band">
                  {chips.map((text, i) => (
                    <span key={`${z.key}-${i}`} className="evidence-chip">{text}</span>
                  ))}
                </div>
              )}
            </div>
          );
        })}

      </div>

      {/* Always-rendered sibling of .zone-layer__inner, deliberately NOT
          nested inside it: that div's pan/zoom `transform` establishes its
          own stacking context (CSS: any transform other than `none` does
          this, independent of z-index), which would trap this layer's
          interactive children below react-flow's renderer no matter what
          z-index they carried — the same escape problem the outer layer's
          own (now-auto) z-index solves one DOM level up. This wrapper
          mirrors the same translate/scale transform (so canvas-space
          coordinates still line up) but is the only thing in this whole
          layer with an explicit z-index (ZoneLayer.css
          `.zone-layer__interactive-layer`), so only its own children escape
          to compete with react-flow's renderer (z-index 4) — the
          deck/lavagna panels above stay exactly where they were. See the
          isScenarioMode comment above for the full click-through story. */}
      <div
        className="zone-layer__interactive-layer"
        style={{ transform: `translate(${x}px, ${y}px) scale(${zoom})` }}
      >
        {onExportArea && (
          <>
            <button
              type="button"
              className="zone-layer__export-btn"
              style={{ left: B.deck.x + B.deck.w - 20, top: B.deck.y + 20 }}
              title={t('canvas.exportArea.deck')}
              aria-label={t('canvas.exportArea.deck')}
              onClick={() => onExportArea('deck', 'mazzo')}
            >
              ⬇
            </button>
            <button
              type="button"
              className="zone-layer__export-btn"
              style={{ left: B.brainstorm.x + brainstormWidth - 20, top: B.brainstorm.y + 20 }}
              title={t('canvas.exportArea.brainstorm')}
              aria-label={t('canvas.exportArea.brainstorm')}
              onClick={() => onExportArea('brainstorm', 'brainstorming')}
            >
              ⬇
            </button>
            {isScenarioMode && (
              <button
                type="button"
                className="zone-layer__export-btn"
                style={{ left: matrixArea.x + matrixArea.w - 20, top: matrixArea.y + 20 }}
                title={t('canvas.exportArea.matrix')}
                aria-label={t('canvas.exportArea.matrix')}
                onClick={() => onExportArea('matrix', 'matrice')}
              >
                ⬇
              </button>
            )}
            <button
              type="button"
              className="zone-layer__export-btn"
              style={{ left: lavagnaX + B.lavagna.w - 20, top: B.lavagna.y + 20 }}
              title={t('canvas.exportArea.lavagna')}
              aria-label={t('canvas.exportArea.lavagna')}
              onClick={() => onExportArea('lavagna', 'lavagna')}
            >
              ⬇
            </button>
          </>
        )}
        {isScenarioMode && (
          <>
            <ScenarioTimeline
              bounds={B.brainstorm}
              splits={scenarioSplits}
              branches={scenarioBranches}
              onAddSplit={onAddScenarioSplit}
              onAcceptSplit={onAcceptScenarioSplit}
              onDismissSplit={onDismissScenarioSplit}
              onRenameBranch={onRenameScenarioBranch}
              onDeleteBranch={onDeleteScenarioBranch}
              onUpdateBranchStyle={onUpdateBranchStyle}
              onSizeChange={handleScenarioSizeChange}
            />
            <ScenarioMatrix
              bounds={matrixArea}
              splits={scenarioSplits}
              branches={scenarioBranches}
              axisLabels={matrixAxisLabels}
              onAxisLabelChange={onAxisLabelChange}
              onUpdatePos={onUpdateBranchMatrixPos}
            />
          </>
        )}
      </div>
    </div>
  );
}
